/**
 * SHARE — THE BUILT PROPOSAL, PREVIEWED, THEN SAID IN EMMA'S WORDS.
 *
 * Jacky, 19 Sep, on the whole step: *"go next share function, this one generate a preview which is actual
 * proposal built not sent. then wire up communication module if not built build it. and instead of use fix
 * template, get emma to read all content, write a simple offering email at high level, and user choose
 * email or text, when contact dont have email or phone, user can add, and it save to contact and also use
 * to send"*.
 *
 * Four things are deliberately separate here, because conflating them is how a customer gets mailed
 * something nobody read:
 *
 *   1. BUILD — releasing the customer copy. It writes the selling version and mints the private link. It
 *      sends nothing.
 *   2. PREVIEW — the actual built Proposal, in an iframe of the very page the customer opens. Not a
 *      lookalike: the same document, rendered by the same screen, before anybody is told about it.
 *   3. THE WORDS — EmmA reads the whole Proposal (brief, rooms, what is included, the options, the total)
 *      and writes a short, high-level offering message. There is no template to fill in; if she cannot be
 *      reached, the panel says so rather than falling back to a fixed paragraph.
 *   4. THE SEND — he picks email or text, approves, and it is queued through `crm.communication`. A contact
 *      missing an address or a number is a field to fill in here, and it is saved to the customer as well as
 *      used for this message.
 *
 * AND THE SECOND MESSAGE IS NOT THE FIRST (Jacky, 29 Sep: *"Emma says same thing every email which fine for
 * first proposal email, then proposal gets updated, got to be more personalized"*). Two things made it so:
 *   · she was told nothing about earlier messages — the fix is on the backbone (`offeringConversation`), and
 *     this panel now shows what she was told: first message, or a revision with N earlier messages, the
 *     changes, and the customer's reply;
 *   · the sent message was saved with NO link to the Proposal (`job_ref: null, lead_id: null`), so the second
 *     send could not find the first. It is saved with `context_kind: 'proposal'` now.
 * And "Tell EmmA what to change" is his words plus the draft on his screen, posted to the same endpoint —
 * she rewrites in place. No canned sentence assembled from labels.
 */
import {createPdfAttachmentLoader} from '../lib/pdf-attachment';
import {useEffect, useMemo, useState} from 'react';
import {CommunicationDeliveryStatus} from './communication-outcome';
import {Button} from '@aiw/ui';
import {api,client,authHeaders, fetchImpl} from '../lib/api';
import {sendShownDraft} from '../lib/communication-office';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {inputCls} from './job-bits';
import {fetchProposalPdf,openProposalPdf} from '../lib/proposal-pdf';

type Channel = 'email' | 'sms';
import {approvedMessage, removeMoneyLines, type ReleasedSummary} from '../lib/proposal-message';

type Conversation = {
  first_send: boolean;
  version: number | null;
  previous_version: number | null;
  messages_sent: number;
  days_since_last_send: number | null;
  previous_message: {channel: string; subject: string; body: string; sent_at: string} | null;
  customer_replies: {channel: string; at: string; summary: string}[];
  changes: string[];
};

type Offering = {
  /** What EmmA wrote, from the Proposal's own content. */
  subject: string;
  body: string;
  channel: Channel;
  /** Who it is going to, as the customer record holds it today. */
  contact: {id: string; name: string; email: string | null; phone: string | null};
  /** True when the customer has no address (email) / no number (text) for this channel yet. */
  missing: boolean;
  /** The private Proposal link the message will carry. */
  link: string | null;
  /** What she read, so the screen can say what the words are based on. */
  read: {rooms: number; options: number; included: string[]};
  /** Which message this is — first, revision or follow-up — and what she was told about the earlier ones. */
  conversation: Conversation | null;
  /** The released copy the link opens: its version and its EX GST total. The message states THIS amount. */
  released?: {version: number | null; total_ex_gst: number | null; summary?: ReleasedSummary | null};
};


export function ProposalShare({proposalId, contactId: peerContactId, leadId = null, jobRef = null, url, publishedVersion, expectedVersion, pricing, busy, dirty, blocked, total, onRun, onNotice, onReloadUrl}: {
  proposalId: string;
  contactId: string | null;
  /** The work this Proposal belongs to, so the sent message is filed against it and the next send finds it. */
  leadId?: string | null;
  jobRef?: string | null;
  url: string;
  publishedVersion: number | null;
  /** The draft version being released — the command refuses a stale one rather than releasing a guess. */
  expectedVersion: number | null;
  /** The selling projection the CRM built; the command re-validates it against the contract. */
  pricing: unknown | null;
  busy: boolean;
  dirty: boolean;
  /** Why the copy cannot be built yet, if it cannot. */
  blocked: string | null;
  /** The customer-facing total, for the panel's own line. */
  total: number | null;
  onRun: (command: string, args: Record<string, unknown>) => Promise<unknown>;
  onNotice: (message: string) => void;
  onReloadUrl: () => Promise<void>;
}) {
  const {t} = useT();
  const [channel, setChannel] = useState<Channel>('email');
  const [offering, setOffering] = useState<Offering | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [writing, setWriting] = useState(false);
  const [writeError, setWriteError] = useState('');
  const [missingValue, setMissingValue] = useState('');
  /** Bumped to ask again — EmmA writing is a model call, and a slow one is not a broken one. */
  const [askAgain, setAskAgain] = useState(0);
  const [sending, setSending] = useState(false);
  const [savedContact, setSavedContact] = useState<Offering['contact'] | null>(null);
  /** His words for a rewrite, and whether she is rewriting. */
  const [instruction, setInstruction] = useState('');
  const [rewriting, setRewriting] = useState(false);

  const written = /^https?:/.test(url);
  const [pdfBusy, setPdfBusy] = useState(false);
  /** The Proposal as the PDF the customer email carries (lib/proposal-pdf.ts). Needs only the id, so it is offered
   *  whether or not this browser holds the customer's private link (Jacky, 14 October 2026: "where the hell is share step"). */
  async function openPdf() {
    if (!proposalId || pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await openProposalPdf(proposalId, {apiBase: env.apiBase, headers: authHeaders, fetch: fetchImpl, open: (u) => window.open(u, '_blank')});
      if (!r.ok) onNotice?.(r.blocked ? t('proposal.pdfBlocked') : t('proposal.share.pdfFailed', {reason: r.reason}));
    } finally { setPdfBusy(false); }
  }
  /**
   * AN EMPTY REASON IS NOT A BLOCK.
   *
   * `customerRelease` answers `{pricing, blocked}` and says `blocked: ''` when the offer is ready — so
   * testing `blocked !== null` disabled the release button on exactly the Proposals that were ready to
   * release. Found by opening the Share step as staff and finding the only button greyed out with nothing
   * on screen to explain it.
   */
  const blockReason = blocked && blocked.trim() ? blocked : null;
  // Read the recipient independently of EmmA's slower message-writing call. P-reference Proposals
  // can carry a job_ref without a lead_id, so the API resolves the associated lead by either key.
  const contact = offering?.contact ?? savedContact;
  const contactId = contact?.id ?? peerContactId ?? null;
  /**
   * THE PDF WITH THE LINK (report 89bffef3, 16 Oct 2026: "send to customer with the URL and attachment, so both
   * options is provided"). Ticked by default for email; prepared once per released version, shown in the review as the
   * file that leaves, and attached to the saved draft. A text cannot carry a file.
   */
  const [attachPdf, setAttachPdf] = useState(true);
  const [pdfFile, setPdfFile] = useState<{name: string; size: number; content_base64: string; version: number | null} | null>(null);
  const [pdfPreparing, setPdfPreparing] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const wantPdf = channel === 'email' && attachPdf;
  // One request per released version, and it always lets go of "preparing" (lib/pdf-attachment.ts — the Send held
  // forever when the version changed mid-render, Rosie Prom 18 Oct 2026).
  const [pdfErrorVersion, setPdfErrorVersion] = useState<number | null>(null);
  const pdfLoader = useMemo(() => createPdfAttachmentLoader(
    (version, signal) => fetchProposalPdf(proposalId!, `Artemis Flooring Proposal v${version}.pdf`, {apiBase: env.apiBase, headers: authHeaders, fetch: fetchImpl as never, signal}),
    {preparing: setPdfPreparing, file: setPdfFile, error: (e) => { setPdfError(e?.reason ?? ''); setPdfErrorVersion(e?.version ?? null); }},
  ), [proposalId]);
  useEffect(() => () => pdfLoader.cancel(), [pdfLoader]);
  useEffect(() => {
    if (!wantPdf || !written || !proposalId || publishedVersion === null) { pdfLoader.cancel(); return; }
    if (pdfFile?.version === publishedVersion || (pdfError && pdfErrorVersion === publishedVersion)) return;
    pdfLoader.request(publishedVersion);
  }, [pdfLoader, wantPdf, written, proposalId, publishedVersion, pdfError, pdfErrorVersion, pdfFile?.version]);
  /**
   * THE STRATA SCOPE PACK, on a tick (report 89bffef3: "give me a click to select if this proposal need to accompany with
   * strata scope document, if selected the email will be prepared with the strata document attached"). Scope of work, how
   * the floor is fixed, the acoustic report and the two insurance certificates — in the Proposal's format. Off by default.
   */
  const [attachStrata, setAttachStrata] = useState(false);
  const [strataFile, setStrataFile] = useState<{name: string; size: number; content_base64: string; version: number | null} | null>(null);
  const [strataPreparing, setStrataPreparing] = useState(false);
  const [strataError, setStrataError] = useState('');
  const wantStrata = channel === 'email' && attachStrata;
  const [strataErrorVersion, setStrataErrorVersion] = useState<number | null>(null);
  const strataLoader = useMemo(() => createPdfAttachmentLoader(
    (version, signal) => fetchProposalPdf(proposalId!, `Artemis Flooring Strata scope pack v${version}.pdf`, {document: 'strata-pack', incompleteMessage:t('strata.incomplete'), apiBase: env.apiBase, headers: authHeaders, fetch: fetchImpl as never, signal}),
    {preparing: setStrataPreparing, file: setStrataFile, error: (e) => { setStrataError(e?.reason ?? ''); setStrataErrorVersion(e?.version ?? null); }},
  ), [proposalId]);
  useEffect(() => () => strataLoader.cancel(), [strataLoader]);
  useEffect(() => {
    if (!wantStrata || !written || !proposalId || publishedVersion === null) { strataLoader.cancel(); return; }
    if (strataFile?.version === publishedVersion || (strataError && strataErrorVersion === publishedVersion)) return;
    strataLoader.request(publishedVersion);
  }, [strataLoader, wantStrata, written, proposalId, publishedVersion, strataError, strataErrorVersion, strataFile?.version]);
  const strataAttachment = wantStrata && strataFile && strataFile.version === publishedVersion ? [{name: strataFile.name, content_type: 'application/pdf', content_base64: strataFile.content_base64}] : [];
  const pdfAttachment = [...(wantPdf && pdfFile && pdfFile.version === publishedVersion ? [{name: pdfFile.name, content_type: 'application/pdf', content_base64: pdfFile.content_base64}] : []), ...strataAttachment];
  const pdfHolding = (wantPdf && !(pdfFile && pdfFile.version === publishedVersion)) || (wantStrata && !strataAttachment.length);
  const recipient = contact ? (channel === 'email' ? contact.email : contact.phone) : null;
  // ONLY A SAVED ADDRESS IS A RECIPIENT. A value typed into the box is not on the customer yet, and the send
  // command reads the contact — so enabling Approve on it promised a send that the command then refused.
  const target = recipient;
  // THE LINK MUST SHOW WHAT THE MESSAGE SAYS. Unsaved edits, or saved edits not yet released, mean the
  // customer's link still opens the previous version.
  const unreleased = dirty || (expectedVersion !== null && publishedVersion !== null && expectedVersion > publishedVersion);
  const releasedTotal = offering?.released?.total_ex_gst ?? null;
  const link = /^https?:/.test(url) ? url : null;

  useEffect(() => {
    if (!proposalId) return;
    let active = true;
    void (async () => {
      try {
        const r = await fetchImpl(`${env.apiBase}/api/proposals/${proposalId}/offering`, {
          method: 'POST', headers: {...(await authHeaders()), 'content-type': 'application/json'},
          body: JSON.stringify({channel: 'email', contact_only: true}),
        });
        const b = await r.json();
        if (r.ok && active) setSavedContact(b.data?.contact ?? null);
      } catch { /* The offering step still shows its own error if the service is unavailable. */ }
    })();
    return () => { active = false; };
  }, [proposalId]);

  // EMMA READS THE PROPOSAL AND WRITES THE OFFERING. Re-asked when he switches channel, because a text is
  // not an email with the greeting cut off — it is the same offer said in the room a text is read in.
  useEffect(() => {
    // THE PROPOSAL IS WHAT IS ASKED ABOUT, NOT THE CONTACT. Guarding on `contactId` here was a loop that
    // could never start: the contact arrives WITH the answer (the API resolves them from the Proposal's own
    // lead), so waiting for one before asking meant never asking, and the message section never appeared.
    if (!proposalId) return;
    let active = true;
    setWriting(true); setWriteError('');
    void (async () => {
      try {
        // POST, not GET: the private link travels with the request (the token is minted at release and only
        // its hash is stored, so the screen that holds it is the one that hands it over). A token in a query
        // string is a token in every access log on the way.
        const r = await fetchImpl(`${env.apiBase}/api/proposals/${proposalId}/offering`, {
          method: 'POST',
          headers: {...(await authHeaders()), 'content-type': 'application/json'},
          body: JSON.stringify({channel, link: url || null}),
        });
        const b = await r.json();
        if (!r.ok) throw Error(b.error ?? 'EmmA could not write the message');
        if (!active) return;
        const data = b.data as Offering;
        setOffering(data); setSubject(data.subject); setBody(removeMoneyLines(data.body));
      } catch (e) {
        if (active) { setOffering(null); setWriteError((e as Error).message); }
      } finally { if (active) setWriting(false); }
    })();
    return () => { active = false; };
    // `url` is a dependency because RELEASING IS WHAT MAKES THE QUESTION ANSWERABLE: before the customer
    // copy exists the API refuses, and without this the panel would keep showing that refusal after he had
    // just released it — the message only appearing if he happened to switch channel or reload.
    // ASKED ONCE PER (PROPOSAL, CHANNEL, LINK). `contactId` must NOT be a dependency: it arrives WITH the
    // answer, so depending on it asked EmmA a second time the moment her first answer landed — the customer
    // waited two model calls (~80 s) for one message, and watched "EmmA is reading…" reset in between.
  }, [proposalId, channel, url, askAgain]);

  /**
   * TELL EMMA WHAT TO CHANGE. His words and the draft on his screen go to the same endpoint that wrote it,
   * and she rewrites in place — keeping what he did not ask to change. Nothing is saved or sent here.
   */
  async function rewrite() {
    if (!instruction.trim() || rewriting || writing) return;
    setRewriting(true); setWriteError('');
    try {
      const r = await fetchImpl(`${env.apiBase}/api/proposals/${proposalId}/offering`, {
        method: 'POST',
        headers: {...(await authHeaders()), 'content-type': 'application/json'},
        body: JSON.stringify({channel, link: url || null, instruction: instruction.trim(), current_draft: {subject, body}}),
      });
      const b = await r.json();
      if (!r.ok) throw Error(b.error ?? 'EmmA could not rewrite the message');
      const data = b.data as Offering;
      setOffering(data); setSubject(data.subject); setBody(removeMoneyLines(data.body));
      setInstruction('');
      onNotice(t('share.rewritten'));
    } catch (e) {
      setWriteError((e as Error).message);
    } finally { setRewriting(false); }
  }

  async function saveMissing() {
    if (!contactId || !missingValue.trim()) return;
    // SAVED TO THE CUSTOMER, not just used for this message: `update_contact` moves the row and its
    // identity keys together, so the next enquiry from this number finds the same person.
    await onRun('update_contact', channel === 'email' ? {contact_id: contactId, email: missingValue.trim()} : {contact_id: contactId, phone: missingValue.trim()});
    onNotice(t('share.contactSaved'));
    setMissingValue('');
    // Re-ask, so what is shown is what the customer record now holds.
    setWriting(true);
    try {
      const r = await fetchImpl(`${env.apiBase}/api/proposals/${proposalId}/offering`, {
        method: 'POST',
        headers: {...(await authHeaders()), 'content-type': 'application/json'},
        body: JSON.stringify({channel, link: url || null}),
      });
      const b = await r.json();
      if (r.ok) { const data = b.data as Offering; setOffering(data); setSubject(data.subject); setBody(removeMoneyLines(data.body)); }
    } finally { setWriting(false); }
  }

  const [deliveryId,setDeliveryId]=useState<string|null>(null);
  async function approveAndSend() {
    if (!contactId || !target || releasedTotal === null || !link || unreleased) return;
    setSending(true);
    try {
      const approvedBody = approvedMessage(body, {total_ex_gst: releasedTotal, summary: offering?.released?.summary ?? null}, link, channel);
      // THE DRAFT IS SAVED FIRST, then queued. Two separate acts, and the screen shows both — this is
      // communication leaving the building, so it is the one thing that comes back for a click.
      // FILED AGAINST THE PROPOSAL, so the next send can find this one. `context_kind: 'proposal'` is the
      // durable link; the lead and job are added when the command accepts them as this customer's — if it
      // refuses the pairing ("not linked to this customer"), the message still goes, filed by Proposal only.
      const draftArgs = {
        contact_id: contactId, audience_kind: 'customer', audience_id: contactId, mailbox: 'emma', channel,
        context_kind: 'proposal', context_ref: proposalId,
        subject: channel === 'sms' ? (subject.trim() || t('share.smsSubject')) : subject.trim(),
        body: approvedBody, attachments: channel === 'email' ? pdfAttachment : [], reply_id: null,
      };
      const saved = await onRun('save_communication', {...draftArgs, job_ref: jobRef, lead_id: leadId})
        .catch(async (e: unknown) => /not linked/i.test((e as Error).message ?? '') ? onRun('save_communication', {...draftArgs, job_ref: null, lead_id: null}) : Promise.reject(e)) as {draft_id?: string} | undefined;
      const draftId = saved?.draft_id;
      if (!draftId) throw Error(t('share.draftNotSaved'));
      // The click is the verdict (ADR-024): one request opens, decides and executes the send case.
      const sent=await sendShownDraft(draftId,{channel,mailbox:'emma',body:approvedBody});
      if(sent.status!=='executed')throw Error(sent.status==='denied'?sent.reason:t('delivery.unavailable'));
      if((sent.result as {state?:string}|undefined)?.state!=='queued')throw Error(t('delivery.unavailable'));
      setDeliveryId(draftId);
      onNotice(channel === 'sms' ? t('share.queuedText', {to: target}) : t('share.queuedEmail', {to: target}));
    } catch (e) {
      onNotice(`${t('share.sendFailed')}: ${(e as Error).message}`);
    } finally { setSending(false); }
  }

  return <div className="space-y-5">
    {deliveryId&&<CommunicationDeliveryStatus id={deliveryId}/>}

    <div>
      <h2 className="font-semibold">{t('proposal.share.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('proposal.share.blurb')}</p>
    </div>

    {/* 1 · BUILD — the customer copy. Nothing leaves. */}
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h3 className="text-sm font-medium">{t('share.step.build')}</h3>
      {savedContact && <p className="text-sm">{savedContact.email ? t('share.toEmail', {to: savedContact.email}) : t('share.needEmail')}</p>}
      {blockReason && <p className="text-sm" role="alert">{blockReason}</p>}
      {!blocked && total !== null && <p className="num text-sm">{t('proposal.share.totalExGst', {amount: total.toLocaleString('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2})})}</p>}
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy || dirty || blockReason !== null || expectedVersion === null}
          onClick={() => void onRun('share_customer_proposal', {id: proposalId, expected_version: expectedVersion, operation: 'publish', ...(pricing ? {pricing} : {})})}>
          {publishedVersion ? t('proposal.share.republish') : t('proposal.share.publish')}
        </Button>
        {publishedVersion !== null && <Button variant="outline" disabled={busy || dirty || expectedVersion === null}
          onClick={() => void onRun('share_customer_proposal', {id: proposalId, expected_version: expectedVersion, operation: 'revoke'})}>
          {t('proposal.share.revoke')}
        </Button>}
        {/* Open-book (Jacky, 15 Oct 2026, for a builder): the backbone prices every line at catalogue cost and adds the margin once at the total; the private link is the same. */}
        <Button variant="outline" disabled={busy || dirty || expectedVersion === null}
          onClick={() => void onRun('publish_open_book_proposal', {id: proposalId, expected_version: expectedVersion})}>
          {t('proposal.share.openBook')}
        </Button>
      </div>
      {dirty && <p className="text-sm text-muted-foreground">{t('proposal.share.saveFirst')}</p>}
      {publishedVersion!==null&&!written&&<p className="text-sm"><button type="button" className="text-primary underline disabled:opacity-50" disabled={pdfBusy} onClick={()=>void openPdf()}>{pdfBusy?t('proposal.share.pdfDrawing'):t('proposal.share.printPdf')}</button> <span className="text-muted-foreground">· v{publishedVersion}</span></p>}
      {publishedVersion!==null&&!written&&<p className="text-sm">{t('proposal.link.loading')} <button type="button" className="text-primary underline" onClick={()=>void onReloadUrl()}>{t('proposal.link.retry')}</button></p>}
    </section>

    {/* 2 · PREVIEW — the actual built Proposal, in the customer's own page. Not sent. */}
    {written && <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{t('share.step.preview', {version: String(publishedVersion ?? '—')})}</h3>
        <div className="flex gap-4"><a className="text-sm text-primary underline" href={url} target="_blank" rel="noreferrer">{t('proposal.share.view')}</a>
        <button type="button" className="text-sm text-primary underline disabled:opacity-50" disabled={pdfBusy} onClick={()=>void openPdf()}>{pdfBusy?t('proposal.share.pdfDrawing'):t('proposal.share.printPdf')}</button></div>
      </div>
      <p className="text-sm text-muted-foreground">{t('share.previewNote')}</p>
      <iframe src={url} title={t('share.previewTitle')} className="h-[70vh] w-full rounded border bg-white" />
      <div className="flex gap-2">
        <input aria-label="Private Proposal URL" className={`${inputCls} w-full`} readOnly value={url} />
        <Button variant="outline" onClick={() => void navigator.clipboard.writeText(url).then(() => onNotice(t('proposal.share.copied'))).catch(() => onNotice(t('proposal.share.copyHint')))}>{t('proposal.share.copy')}</Button>
      </div>
    </section>}

    {/* 3 + 4 · THE WORDS, THE CHANNEL, THE SEND.
        RENDERED AS SOON AS THE COPY EXISTS, contact or not: a failure to write has to be VISIBLE. Gating the
        whole section on the contact hid EmmA's own error behind the thing that had failed, so the step the
        panel exists for simply did not appear and nothing on screen said why. */}
    {written && <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-medium">{t('share.step.message')}</h3>
        {(['email', 'sms'] as const).map((c) => <label key={c} className="flex items-center gap-2 text-sm">
          <input type="radio" name="share-channel" checked={channel === c} onChange={() => { setChannel(c); setMissingValue(''); }} />
          {c === 'email' ? t('share.channel.email') : t('share.channel.sms')}
        </label>)}
      </div>
      <p className="text-sm text-muted-foreground">{writing ? t('share.emmaWriting') : offering ? t('share.emmaRead', {rooms: String(offering.read.rooms), options: String(offering.read.options), included: offering.read.included.slice(0, 3).join(', ') || t('share.none')}) : ''}</p>
      {offering && !writing && offering.conversation && <div className="rounded border bg-background p-3 text-sm">
        <p>{offering.conversation.first_send
          ? t('share.sequence.first')
          : offering.conversation.changes.length > 0
            ? t('share.sequence.revised', {count: String(offering.conversation.messages_sent), days: String(offering.conversation.days_since_last_send ?? '—')})
            : t('share.sequence.resend', {count: String(offering.conversation.messages_sent), days: String(offering.conversation.days_since_last_send ?? '—')})}</p>
        {offering.conversation.changes.length > 0 && <p className="mt-1 text-muted-foreground">{t('share.changes', {changes: offering.conversation.changes.join(' ')})}</p>}
        {offering.conversation.customer_replies.slice(0, 1).map((r) => <p key={r.at} className="mt-1 text-muted-foreground">{t('share.reply', {summary: r.summary})}</p>)}
      </div>}

      {!writing && !offering && writeError && <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm" role="alert">{writeError}</p>
        <Button variant="outline" size="sm" onClick={() => setAskAgain((n) => n + 1)}>{t('share.askAgain')}</Button>
      </div>}

      {offering && !writing && <>
        {channel === 'email' && <label className="block text-sm">{t('share.subject')}
          <input className={`${inputCls} mt-1 w-full`} value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
        </label>}
        <label className="block text-sm">{channel === 'email' ? t('share.body') : t('share.bodySms', {count: String(body.length)})}
          <textarea className={`${inputCls} mt-1 w-full`} rows={channel === 'email' ? 10 : 4} value={body} maxLength={channel === 'sms' ? 480 : 4000} onChange={(e) => setBody(removeMoneyLines(e.target.value))} />
        </label>
        {/* EXACTLY WHAT LEAVES — the review shows the final text, with the released amount and the link. */}
        <div className="rounded border bg-background p-3 text-sm">
          <p className="font-medium">{t('share.finalMessage', {version: String(offering.released?.version ?? publishedVersion ?? '—')})}</p>
          <pre className="mt-1 whitespace-pre-wrap font-sans">{approvedMessage(body, {total_ex_gst: releasedTotal, summary: offering?.released?.summary ?? null}, link, channel)}</pre>
          {channel === 'email' && <div className="mt-2 border-t pt-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={attachPdf} onChange={(e) => { setAttachPdf(e.target.checked); setPdfError(''); }} />{t('share.attachPdf')}</label>
            <p className="text-micro text-muted-foreground">{t('share.attachPdfHint')}</p>
            {attachPdf && (pdfPreparing ? <p className="mt-1 text-muted-foreground">{t('share.pdfPreparing')}</p>
              : pdfError ? <p className="mt-1" role="alert">{t('share.pdfFailed', {error: pdfError})} <button type="button" className="text-primary underline" onClick={() => setPdfError('')}>{t('share.pdfRetry')}</button></p>
              : pdfFile ? <p className="mt-1"><button type="button" className="text-primary underline" onClick={() => void openPdf()}>{t('share.pdfReady', {name: pdfFile.name, size: `${Math.max(1, Math.round(pdfFile.size / 1024))} KB`})}</button></p> : null)}
            <label className="mt-2 flex items-center gap-2"><input type="checkbox" checked={attachStrata} onChange={(e) => { setAttachStrata(e.target.checked); setStrataError(''); }} />{t('share.attachStrata')}</label>
            <p className="text-micro text-muted-foreground">{t('share.attachStrataHint')}</p>
            {attachStrata && (strataPreparing ? <p className="mt-1 text-muted-foreground">{t('share.pdfPreparing')}</p>
              : strataError ? <p className="mt-1" role="alert">{t('share.pdfFailed', {error: strataError})} <button type="button" className="text-primary underline" onClick={() => setStrataError('')}>{t('share.pdfRetry')}</button></p>
              : strataFile ? <p className="mt-1"><a className="text-primary underline" href={`data:application/pdf;base64,${strataFile.content_base64}`} download={strataFile.name}>{t('share.pdfReady', {name: strataFile.name, size: `${Math.max(1, Math.round(strataFile.size / 1024))} KB`})}</a></p> : null)}
          </div>}
          {releasedTotal === null && <p className="mt-1" role="alert">{t('share.noReleasedAmount')}</p>}
        </div>

        <label className="block text-sm">{t('share.instruction')}
          <textarea className={`${inputCls} mt-1 w-full`} rows={2} value={instruction} maxLength={2000} disabled={rewriting} placeholder={t('share.instructionHint')} onChange={(e) => setInstruction(e.target.value)} />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" disabled={rewriting || sending || !instruction.trim()} onClick={() => void rewrite()}>{rewriting ? t('share.rewriting') : t('share.rewrite')}</Button>
          {writeError && !writing && <p className="text-sm" role="alert">{writeError}</p>}
        </div>

        {offering.missing
          ? <div className="space-y-2 rounded border p-3">
            <p className="text-sm font-medium">{channel === 'email' ? t('share.needEmail') : t('share.needPhone')}</p>
            <div className="flex flex-wrap gap-2">
              <input className={`${inputCls} w-72`} value={missingValue} inputMode={channel === 'sms' ? 'tel' : 'email'}
                placeholder={channel === 'email' ? 'name@example.com' : '04xx xxx xxx'} onChange={(e) => setMissingValue(e.target.value)} />
              <Button variant="outline" disabled={!missingValue.trim()} onClick={() => void saveMissing()}>{t('share.saveToContact')}</Button>
            </div>
            <p className="text-sm text-muted-foreground">{t('share.savedBothUses')}</p>
            <p className="text-sm text-muted-foreground">{t('share.saveRecipientFirst')}</p>
          </div>
          : <p className="text-sm">{channel === 'email' ? t('share.toEmail', {to: target ?? ''}) : t('share.toPhone', {to: target ?? ''})}</p>}

        {unreleased && <p className="text-sm" role="alert">{t('share.releaseFirst')}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={sending || !body.trim() || !target || releasedTotal === null || !link || unreleased || pdfHolding} onClick={() => void approveAndSend()}>
            {sending ? t('common.saving') : channel === 'sms' ? t('share.approveText') : t('share.approveEmail')}
          </Button>
          <span className="text-sm text-muted-foreground">{t('share.approveNote')}</span>
        </div>
      </>}
    </section>}
  </div>;
}
